import { TrendingUp, AlertCircle, Clock, CheckCircle2, ShieldAlert, User } from 'lucide-react'

type SlaStatus = 'Breached' | 'At Risk' | 'On Track'
type Priority = 'Critical' | 'High' | 'Medium' | 'Low'

interface EscalationTicket {
  id: string
  title: string
  site: string
  assignee: string
  priority: Priority
  slaStatus: SlaStatus
  responseTime: string
  deadline: string
  escalatedTo: string
}

const PLACEHOLDER_TICKETS: EscalationTicket[] = [
  {
    id: 'ESC-101',
    title: 'Unauthorized access attempt — Server Room door (badge rejected twice)',
    site: 'Site Alpha — Floor 3',
    assignee: 'Ahmed Raza',
    priority: 'Critical',
    slaStatus: 'Breached',
    responseTime: '2h 55m',
    deadline: '30m SLA',
    escalatedTo: 'Security Manager',
  },
  {
    id: 'ESC-102',
    title: 'Perimeter fence breach detected — Zone C, north corner',
    site: 'Site Alpha — Perimeter',
    assignee: 'Hamza Sheikh',
    priority: 'Critical',
    slaStatus: 'At Risk',
    responseTime: '22m',
    deadline: '30m SLA',
    escalatedTo: 'Shift Commander',
  },
  {
    id: 'ESC-103',
    title: 'CCTV camera offline — Parking Level B2, column 4',
    site: 'Parking Level B2',
    assignee: 'Sara Khan',
    priority: 'High',
    slaStatus: 'Breached',
    responseTime: '1h 40m',
    deadline: '1h SLA',
    escalatedTo: 'Control Room Supervisor',
  },
  {
    id: 'ESC-104',
    title: 'Tailgating incident at Gate 2 — identity of second entrant unknown',
    site: 'Gate 2 — Site Alpha',
    assignee: 'Omar Farooq',
    priority: 'High',
    slaStatus: 'On Track',
    responseTime: '14m',
    deadline: '1h SLA',
    escalatedTo: 'Access Control Lead',
  },
  {
    id: 'ESC-105',
    title: 'Unattended bag at Loading Bay — K9 unit not yet on-site',
    site: 'Loading Bay',
    assignee: 'Zainab Qureshi',
    priority: 'High',
    slaStatus: 'At Risk',
    responseTime: '38m',
    deadline: '45m SLA',
    escalatedTo: 'Operations Director',
  },
  {
    id: 'ESC-106',
    title: 'Night shift handover overdue — officer unresponsive on radio',
    site: 'Gate 2 — Site Alpha',
    assignee: 'Bilal Memon',
    priority: 'Medium',
    slaStatus: 'On Track',
    responseTime: '10m',
    deadline: '2h SLA',
    escalatedTo: 'Shift Supervisor',
  },
]

const KPI_CARDS = [
  { label: 'SLA Breaches Today',  value: '2',   icon: AlertCircle,  color: 'text-red-500'    },
  { label: 'Active Escalations',  value: '6',   icon: TrendingUp,   color: 'text-orange-500' },
  { label: 'Avg Response Time',   value: '54m', icon: Clock,        color: 'text-yellow-500' },
  { label: 'Compliance Rate',     value: '67%', icon: CheckCircle2, color: 'text-green-500'  },
]

const SLA_STYLES: Record<SlaStatus, string> = {
  Breached:   'bg-red-50 text-red-700 ring-1 ring-red-200',
  'At Risk':  'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-200',
  'On Track': 'bg-green-50 text-green-700 ring-1 ring-green-200',
}

const PRIORITY_STYLES: Record<Priority, string> = {
  Critical: 'text-red-600',
  High:     'text-orange-500',
  Medium:   'text-yellow-600',
  Low:      'text-gray-400',
}

const PRIORITY_DOT: Record<Priority, string> = {
  Critical: 'bg-red-500',
  High:     'bg-orange-400',
  Medium:   'bg-yellow-400',
  Low:      'bg-gray-300',
}

export function SlaEscalationPage(): JSX.Element {
  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">SLA & Escalation</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor security incident response times and escalation compliance
          </p>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500">
          <Clock className="h-4 w-4" />
          <span>Today</span>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {KPI_CARDS.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4">
            <Icon className={`mt-0.5 h-5 w-5 ${color}`} />
            <div>
              <p className="text-2xl font-bold leading-tight text-gray-900">{value}</p>
              <p className="text-sm text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-700">Active Escalations</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">
                <th className="px-5 py-3">ID</th>
                <th className="px-5 py-3">Incident</th>
                <th className="px-5 py-3">Site / Post</th>
                <th className="px-5 py-3">Priority</th>
                <th className="px-5 py-3">SLA Status</th>
                <th className="px-5 py-3">Response</th>
                <th className="px-5 py-3">Assignee</th>
                <th className="px-5 py-3">Escalated To</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {PLACEHOLDER_TICKETS.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-mono text-[11px] font-medium text-gray-400">
                    {ticket.id}
                  </td>
                  <td className="max-w-xs px-5 py-3 font-medium text-gray-800">{ticket.title}</td>
                  <td className="px-5 py-3 text-gray-500">{ticket.site}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${PRIORITY_STYLES[ticket.priority]}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[ticket.priority]}`} />
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${SLA_STYLES[ticket.slaStatus]}`}>
                      {ticket.slaStatus}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500">{ticket.responseTime}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                      <User className="h-3 w-3" />
                      {ticket.assignee}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs font-medium text-gray-700">{ticket.escalatedTo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
